import { computed, inject } from 'vue'

import type { RbacController, UseRbacReturn } from '../types/runtime'

import { RBAC_KEY } from '../keys'

/**
 * Lifecycle/control surface for the RBAC layer: status, version, connectivity,
 * and the imperative `refresh()`/`clear()` levers (impersonation, logout).
 *
 * `identity` is the tenant/user the held abilities belong to. It is exposed so a
 * host app can assert it against its own authenticated user — the payload's
 * `subject` exists for exactly that sanity check. Note the controller already
 * discards rules on an identity change by itself; reading this is for surfacing
 * the fact, not for enforcing it.
 *
 * For the checks themselves use `useCan()` — `can()` for the CASL view, `has()`
 * for the canonical `permissions[]` view.
 */
export const useRbac = (): UseRbacReturn => {
  const controller = inject<RbacController>(RBAC_KEY)

  if (!controller) {
    throw new Error('[rbac] useRbac() must be called after the RBAC plugin is installed')
  }

  return {
    status: controller.status,
    version: controller.version,
    permissions: controller.permissions,
    identity: controller.identity,
    meta: controller.meta,
    online: controller.online,
    refresh: controller.refresh,
    clear: controller.clear,
    isReady: computed(() => controller.status.value === 'ready')
  }
}
